import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import KaTeXComponent from '../../../../components/KaTeXComponent/KaTeXComponent';
import { Button } from '../../../../components/forms/Buttons/Button';
import {
  postSaveQuestion,
  postSaveQuestionAsDraft,
} from '../../../../services/MyQuestions/QuestionService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DifficultyBadge } from '../../../../components/Difficulty/Difficulty';
import { draftFailureMessage, failedDrafts, savedDraftIndexes } from '../../../../utils/draftSave';
import {
  getSaveErrorMessage,
  incompleteByIndex,
  missingErrors,
  validateQuestions,
} from '../Questions/QuestionUtils';
import { NotSavedNotice, PrintedPassageNotice, bookletRefusal } from '../Questions/EditNotices';
import { forgetPassage } from '../../../../components/Passages/usePassage';
import { isSubjectFolder } from '../../../../utils/folders';
import { getApiErrorCode } from '../../../../utils/apiError';
import { UserContext } from '../../../../utils/contexts/UserContext';
import { BookletLinks, bookletLockKind } from '../../../QuestionPage/review/bookletLock';

// `onIncomplete(errorsById)`: questions of an exam's subject lack what review needs (found here, or by the
// server's 400 incomplete): the page goes back to the questions step with those fields marked.
// `passageBooklets`: the active BTS booklets that print the passage the questions are written for (its detail's
// `booklets`): none can join it now, so creating waits (said next to the button) while drafts can be saved.
export default function Final({ data, handleData, folder, passage, passageBooklets = [], back, onIncomplete }) {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext) || {};
  const [isSaving, setIsSaving] = useState(false);
  const passageLocked = passageBooklets.length > 0;
  // Written for a passage that an active BTS booklet prints, the questions are refused (409 question_in_booklet) and
  // none is created. Said above the buttons with the way out, while the questions stay here, to be saved as drafts or
  // created once the booklet is back in draft: {message, booklets} (their links, for an admin, who returns them).
  const [bookletLock, setBookletLock] = useState(null);
  const inSubject = isSubjectFolder(folder);
  // The subject picked on the first step goes with every question (also into drafts, so it is sent on submit).
  const questionsWithSubject = () => data.questions.map((q) => ({ ...q, subject_uuid: data.subject }));
  // Created inside an exam's subject (?folder=): folder_uuid goes with each created question, plus the subject
  // unless a subtopic is sent (the server takes the subject from it); a draft takes the folder as `folder`.
  // Written for a passage (?passage=): passage_uuid links each created question to it, after its current
  // questions and in this order. Drafts keep it in their data, like the subject, so it is sent on submit.
  const questionsToCreate = () => {
    const questions = folder
      ? questionsWithSubject().map(({ subject_uuid, ...q }) => ({
          ...q,
          folder_uuid: folder.uuid,
          ...(q.subtopic ? {} : { subject_uuid }),
        }))
      : questionsWithSubject();
    return passage ? questions.map((q) => ({ ...q, passage_uuid: passage.uuid })) : questions;
  };

  // Back to the questions with the fields marked. `errorsByIndex`: by position in data.questions (the order
  // sent), turned into the question ids the questions step keeps its errors by.
  const backToFix = (errorsByIndex) => {
    const positions = Object.keys(errorsByIndex);
    toast.error(
      data.questions.length > 1
        ? t('my_questions.validation.error_on_questions', { numbers: positions.map((i) => +i + 1).join(', ') })
        : t('errors.incomplete'),
    );
    onIncomplete?.(
      Object.fromEntries(
        positions
          .filter((i) => data.questions[i])
          .map((i) => [data.questions[i].id, errorsByIndex[i]]),
      ),
    );
  };

  return (
    <>
      <div className="mx-auto flex w-full max-w-4xl flex-col items-stretch gap-4">
        {data?.questions?.map((question, n) => (
          <div key={question.id ?? n} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-row flex-wrap gap-2 text-sm">
              {[
                data?.purposes?.find((p) => p.uuid === question?.purpose)?.name,
                data?.topics?.find((p) => p.uuid === question?.topic)?.name ?? question?.topic_name,
                question?.subtopic_name,
                data?.objectives?.find((p) => p.uuid === question?.objective)
                  ?.name,
              ]?.map((d, i) => (
                <span
                  key={i}
                  className="inline-flex max-w-fit items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 empty:hidden"
                >
                  {d}
                </span>
              ))}
              <DifficultyBadge level={question?.difficulty} />
            </div>
            <div className="text-base font-medium text-slate-900">
              <KaTeXComponent text={`${n + 1}. ${question?.body ?? ''}`} />
            </div>
            {question?.answer_keys.map((answer, i) => (
              <div key={i} className="flex flex-row items-center gap-3">
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    answer.is_correct
                      ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/25'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {answer.option}
                </div>
                <span className="min-w-0 text-sm text-slate-700">
                  <KaTeXComponent text={answer.body} />
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      {passageLocked && !bookletLock && (
        <div className="mx-auto w-full max-w-4xl">
          <PrintedPassageNotice booklets={passageBooklets} id="create-passage-lock" />
        </div>
      )}
      {bookletLock && (
        <div className="mx-auto w-full max-w-4xl">
          <NotSavedNotice
            title={t('my_questions.create.not_created_title', { count: data.questions.length })}
            message={
              <>
                {bookletLock.message}
                <BookletLinks booklets={bookletLock.booklets} />
                <span className="mt-1 block">
                  {t('passages.draft.save_later', { button: t('my_questions.actions.save_draft_and_exit') })}
                </span>
              </>
            }
          />
        </div>
      )}
      {/* Phones stack the two (44px), the main one on top; wider screens keep them in a row on the right. */}
      <div className="mx-auto flex w-full max-w-4xl flex-col-reverse gap-2 border-t border-slate-200 pb-6 pt-6 sm:flex-row sm:justify-end">
        {back && (
          <Button
            className="max-sm:h-11 sm:mr-auto"
            text={t('buttons.back')}
            Icon={ArrowLeftIcon}
            color="secondary-outline"
            disabled={isSaving}
            onClick={back}
          />
        )}
        <Button
          className="max-sm:h-11"
          text={t('my_questions.actions.save_draft_and_exit')}
          color="secondary-outline"
          loading={isSaving}
          disabled={isSaving}
          onClick={(e) => {
            if (e.detail > 1) return;
            setIsSaving(true);
            // A draft written in an exam's subject is kept there (as closed as its questions).
            const draft_questions = questionsWithSubject().map((q) => ({
              draft: passage ? { ...q, passage_uuid: passage.uuid } : q,
              ...(folder ? { folder: folder.uuid } : {}),
            }));
            postSaveQuestionAsDraft(draft_questions)
              .then((results) => {
                const failed = failedDrafts(results);
                if (!failed.length) {
                  toast.success(t('my_questions.toasts.saved_as_draft'));
                  navigate('/my-questions');
                  return;
                }
                // Refused drafts stay on the page; the saved ones leave the list, so a retry does not save them twice.
                const saved = savedDraftIndexes(results);
                if (saved.size) handleData?.({ questions: data.questions.filter((_, i) => !saved.has(i)) });
                const message = draftFailureMessage(t, failed, draft_questions.length);
                toast.error(saved.size ? `${message} ${t('my_questions.toasts.drafts_saved_rest')}` : message);
              })
              .catch((e) => {
                toast.error(getSaveErrorMessage(t, e));
              })
              .finally(() => {
                setIsSaving(false);
              });
          }}
        />
        <Button
          className="max-sm:h-11 sm:px-8"
          text={t('my_questions.actions.finish_and_create')}
          color="primary"
          disabled={isSaving || passageLocked}
          loading={isSaving}
          aria-describedby={passageLocked && !bookletLock ? 'create-passage-lock' : undefined}
          onClick={(e) => {
            if (e.detail > 1) return;
            // In an exam's subject every question must be complete (the server takes all of them or none).
            if (inSubject) {
              const errors = validateQuestions(data.questions, { inSubject });
              if (Object.keys(errors).length) {
                backToFix(errors);
                return;
              }
            }
            setIsSaving(true);
            setBookletLock(null);
            postSaveQuestion(questionsToCreate())
              .then(() => {
                toast.success(t('my_questions.toasts.questions_saved'));
                if (passage) {
                  // Back to the passage, which now lists the new questions (its cached detail is outdated).
                  forgetPassage(passage.uuid);
                  navigate(`/questions/texts?open=${passage.uuid}`);
                } else {
                  navigate('/my-questions');
                }
              })
              .catch((e) => {
                // 400 incomplete, items: [{index, missing}] by position in the list sent: none was created.
                const incomplete = incompleteByIndex(e);
                if (incomplete && Object.keys(incomplete).length) {
                  backToFix(
                    Object.fromEntries(
                      Object.entries(incomplete).map(([i, missing]) => [i, missingErrors(missing, data.questions[i])]),
                    ),
                  );
                  return;
                }
                // A new question is never printed itself: the booklet prints the passage it joins. The refusal's `lock`
                // says so ("passage"); an older backend sends none, and then a passage is what it can be.
                if (getApiErrorCode(e) === 'question_in_booklet') {
                  setBookletLock(bookletRefusal(t, e, user, bookletLockKind(e) ?? (passage ? 'passage' : 'question')));
                  return;
                }
                toast.error(getSaveErrorMessage(t, e));
              })
              .finally(() => {
                setIsSaving(false);
              });
          }}
        />
      </div>
    </>
  );
}
